import { computed, readonly, ref } from "vue";
import { errorMessageAsString } from "@/utils/simple-error";
const DEFAULT_POLL_DELAY = 10000;
/**
 * Composable for waiting (polling) on generic background tasks or processes.
 */
export function useGenericMonitor(options) {
    let timeout = null;
    let pollDelay = options.defaultPollDelay ?? DEFAULT_POLL_DELAY;
    const isRunning = ref(false);
    const taskStatus = ref();
    const requestId = ref();
    const requestHasFailed = ref(false);
    const isCompleted = computed(() => options.completedCondition(taskStatus.value));
    const hasFailed = computed(() => options.failedCondition(taskStatus.value));
    function isFinalState(status) {
        return options.completedCondition(status) || options.failedCondition(status);
    }
    function loadStatus(storedStatus) {
        taskStatus.value = storedStatus;
    }
    async function waitForTask(taskId, pollDelayInMs) {
        pollDelay = pollDelayInMs ?? pollDelay;
        resetState();
        requestId.value = taskId;
        isRunning.value = true;
        return fetchTaskStatus(taskId);
    }
    async function fetchTaskStatus(taskId) {
        try {
            const result = await options.fetchStatus(taskId);
            taskStatus.value = result;
            if (isCompleted.value || hasFailed.value) {
                isRunning.value = false;
            }
            else {
                pollAfterDelay(taskId);
            }
        }
        catch (err) {
            handleError(errorMessageAsString(err));
        }
    }
    function pollAfterDelay(id) {
        resetTimeout();
        timeout = setTimeout(() => {
            fetchTaskStatus(id);
        }, pollDelay);
    }
    function handleError(err) {
        taskStatus.value = err.toString();
        requestHasFailed.value = true;
        isRunning.value = false;
        resetTimeout();
    }
    function resetTimeout() {
        if (timeout) {
            clearTimeout(timeout);
            timeout = null;
        }
    }
    function resetState() {
        resetTimeout();
        taskStatus.value = undefined;
        requestHasFailed.value = false;
        isRunning.value = false;
    }
    return {
        waitForTask,
        isFinalState,
        loadStatus,
        isRunning: readonly(isRunning),
        isCompleted: readonly(isCompleted),
        hasFailed: readonly(hasFailed),
        requestHasFailed: readonly(requestHasFailed),
        taskStatus: readonly(taskStatus),
        expirationTime: options.expirationTime,
    };
}
